import get from 'lodash/get';
import { matchChromeDataModel } from './car-utils';
import { Condition } from './constants';
import { AccessoryMapping } from './legos/accessoryTypes';
import { CarBody } from './legos/carTypes';

const escapeRegex = (s: string) => s.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&');

export const isAccessoryApplicable = <
  A extends {
    enabled?: boolean;
    conditions?: Condition[];
    yearRange?: number[];
    mapping?: AccessoryMapping;
  },
  C extends {
    model?: string | null;
    pricingMatrixModel?: string;
    body?: CarBody | null;
    year?: number | null;
    make?: string | null;
    condition?: Condition | null;
    trim?: string | null;
  }
>(
  accessory?: A | null,
  car?: C | null,
): boolean => {
  if (!car || !accessory || !accessory.enabled) return false;

  const { year, make, condition } = car;

  // Check if car condition matches
  if (condition) {
    const conditions = accessory.conditions || [];
    if (!conditions.includes(condition)) {
      return false;
    }
  }

  // Match all used cars
  if (condition === Condition.USED) {
    return true;
  }

  // Match year range
  if (year) {
    const { yearRange } = accessory;
    if (yearRange && !(year >= yearRange[0] && year <= yearRange[1])) {
      return false;
    }
  }

  // Check if car matches any of the "All" rules
  const allMatchRules = ['all', `${make}.all`];
  if (allMatchRules.some(key => get(accessory.mapping, key) === true)) {
    return true;
  }

  const yearRule = get(accessory.mapping, `${make}.${year}`) || {};

  // Try to match model name and body
  const modelRules = Object.entries(yearRule)
    .filter(
      ([currentModel, rule]) =>
        !!rule && matchChromeDataModel(car, currentModel || ''),
    )
    .map(([, rule]) => rule);

  // Check if any of the matched models has "All" trims selected
  if (modelRules.some(rule => get(rule, 'all'))) {
    return true;
  }

  // Check if trim exists
  const trim = (car.trim || '').toLowerCase();
  if (!trim) {
    return false;
  }

  // Extract all trims from matched model rules
  const trims = modelRules
    .reduce<string[]>(
      (acc, rule) => [...acc, ...Object.keys(rule as Record<string, boolean>)],
      [],
    )
    .filter(t => !!t)
    .map(t => t.toLowerCase());
  const match =
    trims.find(t => t === trim || t.replace(/\(.*\)/gi, '').trim() === trim) ||
    trims.find(t => new RegExp(`^${escapeRegex(t)}\\b`, 'i').exec(trim));

  return !!match;
};

export const getAccessoryPrice = <
  A extends {
    price?: number;
    discount?: number;
  }
>(
  accessory?: A | null,
): number => {
  if (!accessory) {
    return 0;
  }
  return (accessory.price || 0) * (1 - (accessory.discount || 0) / 100);
};
